import { useEffect, useRef, useState } from "react";
import { api, uploadAndProcess } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader, Empty, Modal, CrudActions } from "../components/Crud";
import MissedLectureMode from "../components/MissedLectureMode";
import { setFocusMode } from "../components/Layout";
import {
    Mic, Square, Pause, Play, Upload, FileText, Brain, Layers, MessageCircle,
    Search, Star, Plus, Sparkles, BookOpen, Clock, GraduationCap,
    Maximize2, Minimize2,
} from "lucide-react";

export default function StudyHub() {
    const { user, effectiveRole } = useAuth();
    const [lectures, setLectures] = useState([]);
    const [q, setQ] = useState("");
    const [moduleFilter, setModuleFilter] = useState("");
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [mode, setMode] = useState("lectures");

    useEffect(() => {
        api.get("/lectures").then(r => setLectures(r.data)).catch(() => {});
    }, []);

    // Honour founder "View As": the projected role is what matters here so a
    // founder previewing as Student can access Study Hub without touching
    // their real account role.
    if ((effectiveRole || user?.role) !== "student") return <div className="p-6">Study Hub is only available to student accounts.</div>;

    if (mode === "missed") return <MissedLectureMode onBack={() => setMode("lectures")} />;

    const modules = Array.from(new Set(lectures.map(l => l.module).filter(Boolean)));
    const filtered = lectures.filter(l => {
        const matchesQ = !q || (`${l.title} ${l.module} ${(l.tags || []).join(" ")} ${l.transcript || ""}`).toLowerCase().includes(q.toLowerCase());
        const matchesMod = !moduleFilter || l.module === moduleFilter;
        return matchesQ && matchesMod;
    });
    const totalNotes = lectures.filter(l => l.notes).length;
    const totalRevision = lectures.filter(l => l.revision).length;
    const totalMinutes = Math.round(lectures.reduce((s, l) => s + (l.duration || 0), 0) / 60);

    const refresh = async () => { const { data } = await api.get("/lectures"); setLectures(data); };

    return (
        <div data-testid="study-hub">
            <PageHeader title="Study Hub" subtitle="record, transcribe, revise"
                action={
                    <div className="flex gap-2">
                        <button onClick={() => setMode("missed")} className="btn-ghost inline-flex items-center gap-2"
                                data-testid="missed-mode-btn">
                            <GraduationCap size={16} /> Missed Lecture Mode
                        </button>
                        <button onClick={() => setOpen(true)} className="btn-primary inline-flex items-center gap-2" data-testid="new-lecture-btn">
                            <Plus size={16} /> New lecture
                        </button>
                    </div>
                } />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5 fade-up">
                <StatTile label="Lectures" value={lectures.length} colour="var(--brand-purple)" icon={BookOpen} />
                <StatTile label="With Notes" value={totalNotes} colour="var(--brand-magenta)" icon={FileText} />
                <StatTile label="Revision sets" value={totalRevision} colour="var(--brand-orange)" icon={Layers} />
                <StatTile label="Recorded minutes" value={totalMinutes} colour="var(--status-green)" icon={Clock} />
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="relative flex-1 min-w-[14rem] max-w-md">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-ink)]/40" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lectures, transcripts, tags…"
                           data-testid="study-search"
                           className="w-full pl-9 pr-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
                </div>
                <select value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}
                        data-testid="study-module-filter"
                        className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm">
                    <option value="">All modules</option>
                    {modules.map(m => <option key={m}>{m}</option>)}
                </select>
            </div>

            {filtered.length === 0 ? (
                <Empty icon={Mic} title="No lectures yet" hint="Record live, or upload an existing audio file (mp3/m4a/wav/webm)." />
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(l => (
                        <button key={l.id} onClick={() => setSelected(l)} data-testid={`lecture-card-${l.id}`}
                                className="card-warm p-4 text-left hover:-translate-y-0.5 transition">
                            <div className="flex justify-between items-start gap-2">
                                <div className="flex-1 min-w-0">
                                    {l.module && <div className="font-script text-[var(--brand-orange)] text-sm">{l.module}</div>}
                                    <div className="font-serif text-lg truncate">{l.title || "Untitled lecture"}</div>
                                </div>
                                {l.favorite && <Star size={14} className="text-[var(--brand-orange)] shrink-0" fill="currentColor" />}
                            </div>
                            <div className="text-xs text-[var(--text-ink)]/55 mt-2 flex items-center gap-3 flex-wrap">
                                <span className="flex items-center gap-1"><Clock size={12} /> {Math.round((l.duration || 0) / 60)} min</span>
                                {l.transcript && <span className="flex items-center gap-1"><FileText size={12} /> transcript</span>}
                                {l.notes && <span className="flex items-center gap-1"><Brain size={12} /> notes</span>}
                                {l.revision && <span className="flex items-center gap-1"><Layers size={12} /> revision</span>}
                            </div>
                        </button>
                    ))}
                </div>
            )}

            <div className="mt-8 card-warm p-4 text-xs text-[var(--text-ink)]/65 flex items-start gap-2" data-testid="academic-integrity-note">
                <Sparkles size={14} className="mt-0.5 text-[var(--brand-magenta)]" />
                <span>Study Hub helps you <strong>revise and understand</strong> lectures. It is intentionally not designed to draft coursework, dissertations, or assessments for direct submission.</span>
            </div>

            {open && <NewLectureModal onClose={() => setOpen(false)} onCreated={async (l) => { await refresh(); setOpen(false); setSelected(l); }} />}
            {selected && <LectureDetail lecture={selected} onClose={() => setSelected(null)} onUpdate={refresh} />}
        </div>
    );
}

function StatTile({ label, value, colour, icon: Icon }) {
    return (
        <div className="card-warm p-4">
            <Icon size={16} style={{ color: colour }} className="mb-2" />
            <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55">{label}</div>
            <div className="font-serif text-3xl" style={{ color: colour }}>{value}</div>
        </div>
    );
}

function NewLectureModal({ onClose, onCreated }) {
    const [title, setTitle] = useState("");
    const [module, setModule] = useState("");
    const [tags, setTags] = useState("");
    const [mode, setMode] = useState("record");
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioFile, setAudioFile] = useState(null);
    const [recording, setRecording] = useState(false);
    const [paused, setPaused] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState("");
    const recRef = useRef(null);
    const chunksRef = useRef([]);
    const intervalRef = useRef(null);

    const start = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
            rec.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: "audio/webm" });
                setAudioBlob(blob);
                stream.getTracks().forEach(t => t.stop());
            };
            rec.start(1000);
            recRef.current = rec;
            setRecording(true); setPaused(false); setSeconds(0);
            intervalRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
        } catch (e) {
            alert("Microphone permission denied: " + e.message);
        }
    };
    const pause = () => { recRef.current?.pause(); setPaused(true); clearInterval(intervalRef.current); };
    const resume = () => { recRef.current?.resume(); setPaused(false); intervalRef.current = setInterval(() => setSeconds(s => s + 1), 1000); };
    const stop = () => { recRef.current?.stop(); setRecording(false); setPaused(false); clearInterval(intervalRef.current); };

    const submit = async () => {
        const src = mode === "record" ? audioBlob : audioFile;
        if (!src) { alert("Add audio first."); return; }
        setBusy(true);
        try {
            // Async upload + background Whisper via /upload-jobs — no 30 s
            // frontend timeout, event loop stays free, UI shows progress.
            const name = mode === "record" ? "lecture.webm" : src.name;
            const audio = mode === "record"
                ? new File([src], name, { type: src.type || "audio/webm" })
                : src;
            const tr = await uploadAndProcess({
                files: audio,
                kind: "audio_transcribe",
                onStatus: (s, info) => {
                    if (s === "uploading") {
                        const pct = typeof info?.percent === "number" ? info.percent : 0;
                        setProgress(`Uploading audio… ${pct}%`);
                    } else if (s === "processing") {
                        setProgress("Transcribing with Whisper…");
                    } else if (s === "complete") {
                        setProgress("Saving lecture…");
                    }
                },
            });
            const tagArr = tags.split(",").map(t => t.trim()).filter(Boolean);
            const payload = {
                title: title || "Untitled lecture",
                module, tags: tagArr,
                transcript: tr.text || "",
                segments: tr.segments || [],
                duration: tr.duration || 0,
                language: tr.language || "",
            };
            const { data: lec } = await api.post("/lectures", payload);
            onCreated?.(lec);
        } catch (e) {
            alert("Failed: " + (e.friendly || e.response?.data?.detail || e.message));
        } finally { setBusy(false); setProgress(""); }
    };

    const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

    return (
        <Modal open onClose={onClose} title="New lecture">
            <div className="grid gap-3" data-testid="new-lecture-form">
                <input placeholder="Title (e.g. Lecture 4 – Kinematics)" value={title} onChange={(e) => setTitle(e.target.value)}
                       data-testid="lecture-title" className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Module (e.g. PHY101)" value={module} onChange={(e) => setModule(e.target.value)}
                           data-testid="lecture-module" className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                    <input placeholder="Tags (comma-separated)" value={tags} onChange={(e) => setTags(e.target.value)}
                           data-testid="lecture-tags" className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setMode("record")} data-testid="lecture-mode-record"
                            className={`text-xs px-3 py-1.5 rounded-full ${mode === "record" ? "brand-gradient text-white" : "btn-ghost"}`}>Record live</button>
                    <button onClick={() => setMode("upload")} data-testid="lecture-mode-upload"
                            className={`text-xs px-3 py-1.5 rounded-full ${mode === "upload" ? "brand-gradient text-white" : "btn-ghost"}`}>Upload audio</button>
                </div>
                {mode === "record" ? (
                    <div className="card-warm p-4 flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${recording && !paused ? "bg-[var(--status-red)] animate-pulse" : "bg-[var(--text-ink)]/20"}`} />
                        <div className="font-mono text-xl">{mmss}</div>
                        <div className="flex-1" />
                        {!recording && !audioBlob && (
                            <button onClick={start} data-testid="rec-start" className="btn-primary text-sm inline-flex items-center gap-2"><Mic size={14} /> Start</button>
                        )}
                        {recording && !paused && (
                            <>
                                <button onClick={pause} className="btn-ghost text-sm inline-flex items-center gap-1"><Pause size={14} /> Pause</button>
                                <button onClick={stop} data-testid="rec-stop" className="btn-primary text-sm inline-flex items-center gap-1"><Square size={14} /> Stop</button>
                            </>
                        )}
                        {recording && paused && (
                            <>
                                <button onClick={resume} className="btn-ghost text-sm inline-flex items-center gap-1"><Play size={14} /> Resume</button>
                                <button onClick={stop} className="btn-primary text-sm">Stop</button>
                            </>
                        )}
                        {!recording && audioBlob && (
                            <span className="text-xs text-[var(--text-ink)]/65">Captured · {(audioBlob.size / 1024).toFixed(0)} KB</span>
                        )}
                    </div>
                ) : (
                    <label className="card-warm p-4 text-sm cursor-pointer hover:bg-[rgba(255,107,53,0.06)] transition flex items-center gap-2">
                        <Upload size={16} />
                        <span>{audioFile ? audioFile.name : "Choose audio file (.mp3, .m4a, .wav, .webm — up to 25 MB)"}</span>
                        <input type="file" accept="audio/*" onChange={(e) => setAudioFile(e.target.files[0])} data-testid="lecture-audio-file" className="hidden" />
                    </label>
                )}
                {progress && <div className="text-xs text-[var(--text-ink)]/70">{progress}</div>}
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="btn-ghost">Cancel</button>
                    <button onClick={submit} disabled={busy} data-testid="lecture-submit-btn" className="btn-primary">
                        {busy ? "Working…" : "Transcribe & Save"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

function LectureDetail({ lecture: initial, onClose, onUpdate }) {
    const [lecture, setLecture] = useState(initial);
    const [tab, setTab] = useState("transcript");
    const [busy, setBusy] = useState(false);
    const [q, setQ] = useState("");
    const [ask, setAsk] = useState("");
    const [askLog, setAskLog] = useState([]);
    const [focus, setFocus] = useState(false);

    // Sync with global focus mode so the app chrome collapses too.
    useEffect(() => {
        setFocusMode(focus);
        return () => setFocusMode(false);
    }, [focus]);

    const save = async (patch) => {
        const { data } = await api.put(`/lectures/${lecture.id}`, patch);
        setLecture(data); onUpdate?.();
    };
    const remove = async () => {
        if (!window.confirm(`Delete lecture "${lecture.title}"?`)) return;
        await api.delete(`/lectures/${lecture.id}`); onUpdate?.(); onClose?.();
    };
    const genNotes = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/ai/lecture-notes", { transcript: lecture.transcript, title: lecture.title });
            await save({ notes: data });
        } finally { setBusy(false); }
    };
    const genRevision = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/ai/lecture-revision", { transcript: lecture.transcript });
            await save({ revision: data });
        } finally { setBusy(false); }
    };
    const askAi = async () => {
        if (!ask.trim()) return;
        const question = ask; setAsk(""); setAskLog(l => [...l, { role: "user", text: question }]);
        try {
            const { data } = await api.post("/ai/lecture-ask", { transcript: lecture.transcript, question });
            setAskLog(l => [...l, { role: "ai", text: data.answer }]);
        } catch (e) {
            setAskLog(l => [...l, { role: "ai", text: "Sorry — " + (e.response?.data?.detail || e.message) }]);
        }
    };

    const segs = lecture.segments || [];
    const filteredSegs = q ? segs.filter(s => s.text.toLowerCase().includes(q.toLowerCase())) : segs;

    // Reading-area sizing:
    //   • Normal: nice large modal with breathing room.
    //   • Focus:  near-fullscreen so long-form AI content maximises its width.
    const shellClass = focus
        ? "card-warm w-full h-full max-h-none rounded-none sm:rounded-none overflow-auto scrollbar-thin"
        : "card-warm w-full sm:max-w-5xl xl:max-w-6xl max-h-[92vh] overflow-auto scrollbar-thin";
    const outerClass = focus
        ? "fixed inset-0 z-50 bg-[var(--bg-cream)] flex"
        : "fixed inset-0 z-50 bg-black/40 grid place-items-end sm:place-items-center p-0 sm:p-6";

    return (
        <div className={outerClass} data-testid="lecture-detail" onClick={focus ? undefined : onClose}>
            <div onClick={(e) => e.stopPropagation()} className={shellClass}>
                <div className="sticky top-0 bg-[var(--bg-cream)] border-b border-[rgba(26,19,24,0.08)] px-4 sm:px-5 py-3 flex items-center gap-2 sm:gap-3 z-10">
                    {lecture.module && <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--brand-orange)]/15 text-[var(--brand-orange)] shrink-0 hidden sm:inline">{lecture.module}</span>}
                    <div className="flex-1 min-w-0">
                        <div className="font-serif text-lg sm:text-xl truncate">{lecture.title}</div>
                        <div className="text-[11px] text-[var(--text-ink)]/55">{Math.round((lecture.duration || 0) / 60)} min · {lecture.language || "en"}</div>
                    </div>
                    <button
                        onClick={() => setFocus(f => !f)}
                        data-testid="lecture-focus-toggle"
                        title={focus ? "Exit focus mode" : "Focus mode"}
                        className="btn-ghost text-xs inline-flex items-center gap-1.5 px-2 py-1.5 shrink-0"
                    >
                        {focus ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                        <span className="hidden sm:inline">{focus ? "Exit focus" : "Focus"}</span>
                    </button>
                    <CrudActions idBase={`lec-${lecture.id}`}
                        isFav={lecture.favorite}
                        onFavorite={() => save({ favorite: !lecture.favorite })}
                        onDelete={remove} />
                    <button onClick={onClose} data-testid="lecture-close-btn" className="btn-ghost text-sm shrink-0">Close</button>
                </div>
                <div className="px-4 sm:px-5 py-4">
                    <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-thin -mx-1 px-1 pb-1">
                        {[
                            { k: "transcript", l: "Transcript", I: FileText },
                            { k: "notes", l: "Smart Notes", I: Brain },
                            { k: "revision", l: "Revision", I: Layers },
                            { k: "ask", l: "Ask AI", I: MessageCircle },
                        ].map(t => (
                            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`lecture-tab-${t.k}`}
                                    className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 whitespace-nowrap ${tab === t.k ? "brand-gradient text-white" : "btn-ghost"}`}>
                                <t.I size={12} /> {t.l}
                            </button>
                        ))}
                    </div>

                    {tab === "transcript" && (
                        <div data-testid="lecture-transcript">
                            <div className="relative mb-2">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-ink)]/40" />
                                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search within transcript…"
                                       className="w-full pl-9 pr-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
                            </div>
                            <div className="space-y-1.5 max-h-[60vh] overflow-y-auto scrollbar-thin">
                                {filteredSegs.length === 0 && lecture.transcript && (
                                    <div className="text-sm whitespace-pre-wrap">{lecture.transcript}</div>
                                )}
                                {filteredSegs.map((s, i) => (
                                    <div key={`seg-${s.start}-${i}`} className="grid grid-cols-[64px_1fr] gap-3 text-sm">
                                        <span className="font-mono text-[var(--brand-purple)] text-xs pt-0.5">{Math.floor(s.start / 60)}:{String(Math.floor(s.start % 60)).padStart(2, "0")}</span>
                                        <span>{s.text}</span>
                                    </div>
                                ))}
                                {filteredSegs.length === 0 && !lecture.transcript && (
                                    <div className="text-sm text-[var(--text-ink)]/55">No transcript yet.</div>
                                )}
                            </div>
                        </div>
                    )}

                    {tab === "notes" && (
                        <div data-testid="lecture-notes">
                            {!lecture.notes ? (
                                <button onClick={genNotes} disabled={busy} data-testid="gen-notes-btn" className="btn-primary inline-flex items-center gap-2">
                                    <Brain size={14} /> {busy ? "Generating…" : "Generate smart notes"}
                                </button>
                            ) : (
                                <div className="space-y-4">
                                    <div className="card-warm p-4">
                                        <div className="font-serif text-lg">{lecture.notes.title || lecture.title}</div>
                                        <div className="text-sm text-[var(--text-ink)]/75 mt-1">{lecture.notes.summary}</div>
                                    </div>
                                    {(lecture.notes.sections || []).map((sec, i) => (
                                        <div key={`sec-${sec.heading || i}`} className="card-warm p-4">
                                            <div className="font-serif text-base mb-1">{sec.heading}</div>
                                            <ul className="text-sm list-disc pl-5 space-y-1">{(sec.bullets || []).map((b, j) => <li key={`b-${i}-${j}`}>{b}</li>)}</ul>
                                        </div>
                                    ))}
                                    {(lecture.notes.key_concepts || []).length > 0 && (
                                        <div className="card-warm p-4">
                                            <div className="font-serif text-base mb-2">Key concepts</div>
                                            <div className="grid sm:grid-cols-2 gap-2">
                                                {lecture.notes.key_concepts.map((k, i) => (
                                                    <div key={`kc-${k.term || i}`} className="text-sm"><span className="font-semibold">{k.term}</span> — <span className="text-[var(--text-ink)]/75">{k.definition}</span></div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {(lecture.notes.potential_exam_topics || []).length > 0 && (
                                        <div className="card-warm p-4 border-l-4" style={{ borderLeftColor: "var(--brand-magenta)" }}>
                                            <div className="font-serif text-base mb-2">Potential exam topics</div>
                                            <ul className="text-sm list-disc pl-5 space-y-1">{lecture.notes.potential_exam_topics.map((t, i) => <li key={`et-${i}-${(t || "").slice(0, 24)}`}>{t}</li>)}</ul>
                                        </div>
                                    )}
                                    <button onClick={genNotes} disabled={busy} className="btn-ghost text-xs">Regenerate</button>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "revision" && (
                        <div data-testid="lecture-revision">
                            {!lecture.revision ? (
                                <button onClick={genRevision} disabled={busy} data-testid="gen-revision-btn" className="btn-primary inline-flex items-center gap-2">
                                    <Layers size={14} /> {busy ? "Generating…" : "Generate revision materials"}
                                </button>
                            ) : (
                                <div className="space-y-5">
                                    {(lecture.revision.flashcards || []).length > 0 && (
                                        <div>
                                            <div className="font-serif text-lg mb-2">Flashcards</div>
                                            <div className="grid sm:grid-cols-2 gap-2">
                                                {lecture.revision.flashcards.map((c, i) => <Flashcard key={`fc-${i}-${(c?.front || "").slice(0, 24)}`} card={c} />)}
                                            </div>
                                        </div>
                                    )}
                                    {(lecture.revision.mcq || []).length > 0 && (
                                        <div>
                                            <div className="font-serif text-lg mb-2">Multiple choice</div>
                                            <div className="space-y-2">
                                                {lecture.revision.mcq.map((m, i) => <MCQ key={`mcq-${i}-${(m?.q || "").slice(0, 24)}`} mcq={m} />)}
                                            </div>
                                        </div>
                                    )}
                                    {(lecture.revision.short_answer || []).length > 0 && (
                                        <div>
                                            <div className="font-serif text-lg mb-2">Short answer</div>
                                            <ul className="space-y-2">
                                                {lecture.revision.short_answer.map((s, i) => (
                                                    <li key={`sa-${i}-${(s?.q || "").slice(0, 24)}`} className="card-warm p-3 text-sm">
                                                        <div className="font-medium">{s.q}</div>
                                                        <details className="mt-1"><summary className="text-xs text-[var(--brand-purple)] cursor-pointer">Reveal model answer</summary>
                                                            <div className="text-[var(--text-ink)]/75 mt-1">{s.model_answer}</div></details>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {lecture.revision.summary && (
                                        <div className="card-warm p-4 border-l-4" style={{ borderLeftColor: "var(--brand-orange)" }}>
                                            <div className="font-serif text-base mb-1">Study summary</div>
                                            <div className="text-sm">{lecture.revision.summary}</div>
                                        </div>
                                    )}
                                    <button onClick={genRevision} disabled={busy} className="btn-ghost text-xs">Regenerate</button>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "ask" && (
                        <div data-testid="lecture-ask">
                            <div className="text-xs text-[var(--text-ink)]/55 mb-2">Ask anything about THIS lecture — answers are restricted to the transcript above.</div>
                            <div className="space-y-2 max-h-[40vh] overflow-y-auto scrollbar-thin mb-3">
                                {askLog.map((m, i) => (
                                    <div key={`ask-${i}-${m.role}`} className={`card-warm p-3 text-sm ${m.role === "user" ? "bg-[rgba(123,45,142,0.05)]" : ""}`}>
                                        <div className="text-[10px] uppercase tracking-wide text-[var(--text-ink)]/55 mb-1">{m.role === "user" ? "You" : "Lecture assistant"}</div>
                                        <div className="whitespace-pre-wrap">{m.text}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <input value={ask} onChange={(e) => setAsk(e.target.value)} onKeyDown={(e) => e.key === "Enter" && askAi()}
                                       placeholder="e.g. Summarise the main argument…" data-testid="ask-input"
                                       className="flex-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
                                <button onClick={askAi} data-testid="ask-send-btn" className="btn-primary text-sm">Ask</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Flashcard({ card }) {
    const [flipped, setFlipped] = useState(false);
    return (
        <button onClick={() => setFlipped(f => !f)} className="card-warm p-4 text-left min-h-[100px] hover:-translate-y-0.5 transition" data-testid="flashcard">
            <div className="text-[10px] uppercase tracking-wide" style={{ color: flipped ? "var(--brand-orange)" : "var(--brand-purple)" }}>{flipped ? "Answer" : "Question"}</div>
            <div className="text-sm mt-1">{flipped ? card.back : card.front}</div>
        </button>
    );
}

function MCQ({ mcq }) {
    const [picked, setPicked] = useState(null);
    const correct = mcq.answer_index;
    return (
        <div className="card-warm p-3 text-sm">
            <div className="font-medium mb-2">{mcq.q}</div>
            <div className="grid sm:grid-cols-2 gap-1.5">
                {(mcq.options || []).map((opt, i) => {
                    const isPicked = picked === i;
                    const showCorrect = picked !== null && i === correct;
                    const showWrong = isPicked && i !== correct;
                    return (
                        <button key={i} onClick={() => setPicked(i)}
                                className={`text-left px-3 py-1.5 rounded-lg border transition ${
                                    showCorrect ? "border-[var(--status-green)] bg-[var(--status-green)]/10" :
                                    showWrong ? "border-[var(--status-red)] bg-[var(--status-red)]/10" :
                                    "border-[rgba(26,19,24,0.12)]"
                                }`}>
                            {String.fromCharCode(65 + i)}. {opt}
                        </button>
                    );
                })}
            </div>
            {picked !== null && mcq.rationale && (
                <div className="text-xs text-[var(--text-ink)]/70 mt-2 italic">{mcq.rationale}</div>
            )}
        </div>
    );
}
